import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useRef, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { customToast } from "../../../components/notify/NotifyContainer";
import { FORM_STATE_OPTIONS } from "../../../constants";
import { AMAZE_WEBSITE_URL } from "../../../types/enum/enum";
import { DEFAULT_HINTS, DEFAULT_LANGUAGE_SETTINGS } from "../../botSettings/kiosk/configs";
import { useUpdateKioskSettingMutation } from "../../botSettings/kiosk/services/kioskApiSlice";
import { IApiHints } from "../../botSettings/kiosk/types";
import { useGetVoicesByLanguageQuery } from "../../voice/services";
import { PROMPT_WORD_LIMIT } from "../constants/constants";
import {
    useCreateCharacterMutation,
    useGetCharacterQuery,
    usePromptListQuery,
    useUpdateCharacterMutation,
    useUploadAvatarMutation,
} from "../services/charactersApiSlice";
import { ICharacterInput, IUpdateCharacterReq } from "../types/interfaces";
import { countPromptWords } from "../utils";
import { CharacterInfoSchema } from "../validations";
import useAiGeneratePrompt from "./useAiGeneratePrompt";
interface IPrompt {
    promptAi: string;
}
export const DEFAULT_VOICE_QUERY = {
    language: "zh-TW",
    gender: "male",
    azure_voice_short_name: "",
};
const defaultValues = {
    name: "",
    greeting: "哈囉，你好",
    prompt: "",
    memory_type: 0,
    description: "",
};
interface IProps {
    isCreate: boolean;
}
const useCharacterForm = ({ isCreate }: IProps) => {
    const { t } = useTranslation();
    const [avatarFile, setAvatarFile] = useState<File | null>(null);
    const { projectId, characterId } = useParams();
    const navigate = useNavigate();
    const lastUpdateSourceRef = useRef<"prompt" | "form" | null>(null);

    // ============== API ==============
    const [uploadAvatar] = useUploadAvatarMutation();
    const [createCharacter, { isLoading: cCLoading, isSuccess: cCSuccess }] = useCreateCharacterMutation();
    const [updateCharacter, { isLoading: uCLoading, isSuccess: uCSuccess }] = useUpdateCharacterMutation();
    const { data: promptsListData } = usePromptListQuery();
    const { data: cInfo } = useGetCharacterQuery({ id: characterId as string }, { skip: !characterId });
    const [prompt, setPrompt] = useState<string>("");

    //------
    const [updateKioskSetting] = useUpdateKioskSettingMutation();
    const promptsOptions =
        promptsListData?.data?.map((p) => ({
            label: p.template_name,
            value: p.id,
            description: p.template_text,
        })) || [];

    const defaultPromptValue = "";
    const defaultPrompt = promptsOptions[0]?.description;
    const promptMethods = useForm<IPrompt>({
        defaultValues: {
            promptAi: "",
        },
        mode: "all",
    });
    const methods = useForm<ICharacterInput>({
        defaultValues: {
            ...defaultValues,
            ...(isCreate && { language: "zh-TW" }),
        },
        resolver: yupResolver(CharacterInfoSchema(isCreate)),
        mode: "all",
    });

    const { data: voices } = useGetVoicesByLanguageQuery(
        { ...DEFAULT_VOICE_QUERY, language: methods.watch("language") },
        { skip: !isCreate || !methods.watch("language") }
    );

    // ============== Data Processing ==============

    const descWordCount = methods.watch("prompt")?.length || 0;
    const isDescWordCountError = methods.formState.errors?.prompt;
    const characterInfo = cInfo?.data?.character;
    const defaultVoiceSetting = voices?.data?.[0];

    // ============== handler ==============

    const fetchUpdate = async (reqData: IUpdateCharacterReq) => await updateCharacter(reqData);

    const fetchUploadAvatar = async () => {
        if (!avatarFile) return;

        try {
            const formData = new FormData();
            formData.append("file", avatarFile);

            // 如果是 update，要帶 character_id，後端會幫忙更改 character 的 avatar_file_name
            if (!isCreate && characterId) {
                formData.append("character_id", characterId);
            }
            return await uploadAvatar(formData).unwrap();
        } catch (err) {
            customToast.error(t("CH.UPLOAD_FAIL") || "上傳失敗，請重試");
        }
    };
    const handleSetAvatarFile = (avatarFile: File) => {
        setAvatarFile(avatarFile);
        methods.setValue("avatarFile", avatarFile, FORM_STATE_OPTIONS);
    };

    const handleFormSubmit: SubmitHandler<ICharacterInput> = async (data) => {
        if (!projectId) return;

        const reqData = {
            ...data,
            project_id: projectId,
            copyable: false,
            img_gen_enabled: false,
            restrict_response: false,
            pitch: 1,
            speed: 1,
        };
        const result = await fetchUploadAvatar();

        if (isCreate && defaultVoiceSetting) {
            try {
                const createCharacterRes = await createCharacter({
                    ...reqData,
                    voice_id: defaultVoiceSetting.id,
                    pitch: defaultVoiceSetting.pitch,
                    speed: defaultVoiceSetting.speed,
                    //description: "",
                    avatar_file_name: result ? result.s3_url : "",
                }).unwrap();
                const createdCharacterId = createCharacterRes?.data?.id;

                // 建立新角色時，會自動更新Kiosk的預設語言與默認提示詞設定，其他設定（跑馬燈、廣告、招呼詞）都會預設為關閉
                if (createdCharacterId) {
                    await updateKioskSetting({
                        character_id: createdCharacterId,
                        native: {
                            ad: {
                                enabled: false,
                                shuffle_mode: false,
                                media: [],
                            },
                            general: {
                                defaultLanguage: DEFAULT_LANGUAGE_SETTINGS.defaultLanguage,
                                languageOptions: DEFAULT_LANGUAGE_SETTINGS.languageOptions,
                            },
                            hints: DEFAULT_HINTS as IApiHints,
                            greetings: {
                                data: [],
                                enabled: false,
                            },
                            schedule_marquee: {
                                data: [],
                                enable_marquee: false,
                                position: "top",
                            },
                        },
                    });
                }
                navigate(`/projects/${projectId}/characters/${createdCharacterId}/character-info`, {
                    state: projectId,
                });
                customToast.success(t("CH.FORM_SUCCESS") || "表單提交成功");
            } catch (err) {
                customToast.error(t("CH.UPLOAD_FAIL") || "上傳失敗，請重試");
            }
        }

        if (characterId && characterInfo) {
            // 因為 update 不需要帶 language，所以這邊先把 language 拿掉
            const { language, ...temp } = reqData;
            const updateReqData = {
                ...characterInfo,
                ...temp,
                ...(result && { avatar_file_name: result.s3_url }),
                id: characterId,
            };
            lastUpdateSourceRef.current = "form";
            await fetchUpdate(updateReqData);
        }
    };

    const handleUpgrade = () => {
        window.open(AMAZE_WEBSITE_URL, "_blank");
    };

    const watchPrompt = promptMethods.watch("promptAi");
    const { generatePrompt, promptLoading, isSuccess: generatePromptSuccess } = useAiGeneratePrompt({ setPrompt });

    const handleCreatePrompt = async () => {
        if (watchPrompt) {
            await generatePrompt(watchPrompt);
        }
    };

    const handlePromptUpdate = useCallback(
        async (newPromptValue: string) => {
            if (countPromptWords(newPromptValue) > PROMPT_WORD_LIMIT) {
                customToast.error(t("CH_INVALID.PROMPT") || "超出提示詞字數上限");
                return;
            }

            if (!characterId || !characterInfo || isCreate) {
                methods.setValue("prompt", newPromptValue, {
                    shouldDirty: true,
                    shouldValidate: true,
                    shouldTouch: true,
                });
                return;
            }

            try {
                const updateReqData: IUpdateCharacterReq = {
                    ...characterInfo,
                    prompt: newPromptValue,
                    id: characterId,
                    project_id: projectId as string,
                };

                lastUpdateSourceRef.current = "prompt";
                await updateCharacter(updateReqData).unwrap();

                methods.setValue("prompt", newPromptValue, {
                    shouldDirty: true,
                    shouldValidate: true,
                    shouldTouch: true,
                });

                customToast.success(t("CH.PROMPT_SUCCESS") || "提示詞更新成功");
            } catch (error) {
                customToast.error(t("CH.PROMPT_FAIL") || "錯誤：無法更新提示詞");
            }
        },
        [characterId, characterInfo, isCreate, projectId, methods, updateCharacter, t]
    );

    const handleConfirmPrompt = async (tabValue: number, currentPromptValue: string | undefined) => {
        try {
            // 根據 tab 類型處理
            switch (tabValue) {
                case 0: // Manual Edit - 直接更新
                    await handlePromptUpdate(currentPromptValue || "");
                    break;

                case 1: // AI Prompt - 生成並更新
                    if (watchPrompt) {
                        await generatePrompt(watchPrompt);
                    } else {
                        await handlePromptUpdate(currentPromptValue || "");
                    }
                    break;

                case 2: // Templates - 直接更新
                    await handlePromptUpdate(currentPromptValue || "");
                    break;

                default:
            }

            return true;
        } catch (error) {
            customToast.error(t("CH.CONFIRM_FAIL") || "確認失敗，請重試");
            return false;
        }
    };
    //>  ============== useEffect ==============

    useEffect(() => {
        if (generatePromptSuccess) {
            promptMethods.reset();
        }
    }, [generatePromptSuccess, promptMethods]);

    useEffect(() => {
        methods.setValue("prompt", prompt, {
            shouldDirty: true,
            shouldValidate: false,
        });
    }, [prompt, methods]);

    useEffect(() => {
        if (!isCreate) {
            return;
        }
        methods.setValue("prompt", defaultPrompt, {
            shouldDirty: true,
            shouldValidate: false,
        });
    }, [isCreate, defaultPrompt, methods]);
    useEffect(() => {
        if (characterInfo) {
            methods.reset({
                name: characterInfo.name,
                greeting: characterInfo.greeting,
                prompt: characterInfo.prompt,
                memory_type: characterInfo.memory_type,
                description: characterInfo?.description,
            });
        }
    }, [characterInfo, methods]);
    useEffect(() => {
        if (generatePromptSuccess) {
            promptMethods.reset();
        }
    }, [generatePromptSuccess, promptMethods]);

    useEffect(() => {
        if (uCSuccess) {
            if (lastUpdateSourceRef.current === "form") {
                customToast.success(t("CH.UPDATE_SUCCESS") || "更新成功");
                methods.reset(methods.getValues());
            }
            lastUpdateSourceRef.current = null;
        }
    }, [uCSuccess, methods, t]);

    return {
        handleSetAvatarFile,
        avatarFile,
        methods,
        handleFormSubmit,
        submitBtnLoading: cCLoading || uCLoading,
        descWordCount,
        isDescWordCountError,
        handleUpgrade,
        promptsOptions,
        defaultPromptValue,
        cCSuccess,
        uCSuccess,
        promptLoading,
        promptMethods,
        handleCreatePrompt,
        handlePromptUpdate,
        handleConfirmPrompt,
    };
};

export default useCharacterForm;
